/**
 * Moves a page that opened on the wrong one of the app's three hosts to the same page on the right
 * one (LP-D5, ADR 0020; #769). Before it, only the retired host's sign-in and invite pages moved (#360;
 * owner ruling S-O35, "Move the pages").
 *
 *   `groundwork-sports.com`       the root domain. Sign-in and invites stay here (LP-D2, LP-D3), so it
 *                                 keeps `/login`, `/signed-in`, `/invite/<code>` and `/first-run`; an app
 *                                 page that still reaches it (an old bookmark of `/coach`: Caddy keeps the
 *                                 app's routes of the day of the move on its list) moves to `app.`.
 *   `app.groundwork-sports.com`   the web app after sign-in. Its `/login` and `/invite/<code>` move to the
 *                                 root domain; everything else stays. A signed-out visitor there is sent
 *                                 to `/login` by the app's own gate, and so lands on the root domain's.
 *   `sport.rieck.pro`             retired, and still served in full (the old TestFlight build calls it
 *                                 with a bearer and never runs this page). Its `/login` and
 *                                 `/invite/<code>` move to the root domain, as before; every other page
 *                                 moves to `app.`.
 *
 * Why the sign-in pages move: signing in with Apple or Google goes out from one origin and comes back
 * to the sign-in service's own, and the service's state cookie belongs to the origin that started it. A
 * sign-in started anywhere but the root domain comes back without it and ends at
 * `/login?error=state_mismatch`. Moving the page BEFORE a sign-in starts makes the round trip happen on
 * one origin. Why the app pages move: the web app's session cookie on `app.` is its own, host-only, set
 * by the hand-off (`handOff.ts`); a cookie for the whole domain is what ADR 0020 refused.
 *
 * What this is not: a redirect of a host. Nothing on the server redirects any of this (Caddy redirects
 * `www.` and nothing else). It is the page's own script, and an API path never runs it.
 *
 * What it carries: the path and the query string (an invite's code is in the path, a refusal's
 * `?error=` in the query). It carries no fragment, so nothing hidden in a `#` crosses to another origin,
 * and nothing the old origin stored (`localStorage` is per origin).
 */

/** The retired host. An exact match: no port, no subdomain, no local name. */
export const RETIRED_HOST = 'sport.rieck.pro'

/** The root domain, where sign-in and invites live. */
export const ROOT_HOST = 'groundwork-sports.com'
export const ROOT_ORIGIN = `https://${ROOT_HOST}`

/** Where the web app lives after sign-in. */
export const APP_HOST = 'app.groundwork-sports.com'
export const APP_ORIGIN = `https://${APP_HOST}`

/** What of `window.location` this reads. */
export interface PageLocation {
  host: string
  pathname: string
  search: string
}

// `/login`, and `/invite/<code>` with exactly one non-empty segment, each with or without a trailing slash:
// the pages a person signs in from, which live on the root domain.
const SIGN_IN_PAGES = /^\/(?:login|invite\/[^/]+)\/?$/

// What the root domain keeps besides those: the hop the sign-in service ends at (it hands the session to
// app., `views/SignedIn.tsx`) and first run.
const ROOT_KEEPS = /^\/(?:login|signed-in|first-run|invite\/[^/]+)\/?$/

// Never a page: a path the SPA does not draw is never moved, whatever the host.
const NOT_A_PAGE = /^\/(?:api|media|\.well-known)(?:\/|$)/

/**
 * The URL to move to, or null when the page stays where it is: the host is none of the three exactly (so
 * never `localhost`, a developer's stack, a `*.localhost` name or `www.`), the path is not a page, or the
 * page belongs where it is.
 */
export function canonicalPageUrl(where: PageLocation): string | null {
  if (NOT_A_PAGE.test(where.pathname)) return null
  const to = (origin: string) => `${origin}${where.pathname}${where.search}`
  switch (where.host) {
    case ROOT_HOST:
      return ROOT_KEEPS.test(where.pathname) ? null : to(APP_ORIGIN)
    case APP_HOST:
      return SIGN_IN_PAGES.test(where.pathname) ? to(ROOT_ORIGIN) : null
    case RETIRED_HOST:
      return to(SIGN_IN_PAGES.test(where.pathname) ? ROOT_ORIGIN : APP_ORIGIN)
    default:
      return null
  }
}

/**
 * The link an invite is shared as: on the root domain, whatever host the admin copied it on (LP-D3). It opens
 * the app on every installed iPhone build, which accepts an invite link of the API's own host only.
 */
export const inviteLink = (code: string): string => `${ROOT_ORIGIN}/invite/${code}`
